import React, { useContext, useEffect, useState } from "react";
import PropTypes from "prop-types";
import ProfileContext from "./ProfileContext";
import useUsers from "../../hooks/useUsers";
import { PAGE_STATE, WORK_PROGRAMS, WORK_RENDERERS } from "../../constants";
import AppContext from "../../AppContext";
import List, { STATE } from "../../components/List";
import SecondaryPagination from "../../components/SecondaryPagination/SecondaryPagination";
import useLikes from "../../hooks/useLikes";
import useWorks from "../../hooks/useWorks";
import useScreenSize from "../../hooks/useScreenSize";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { ReactComponent as UnlikeSvg } from "../../assets/icons/liked-primary.svg";
import { ReactComponent as ShareSvg } from "../../assets/icons/share-primary.svg";
import * as Tooltip from "react-tooltip";
import { toast } from "react-toastify";
import Share from "./ShareProject";

const assetsUri = import.meta.env.ASSETS_URI;

const columns = [
  {
    display: "Project",
    key: "project",
    textAlign: "left",
    width: "120px",
  },
  {
    display: "Shared by",
    key: "createdBy",
    textAlign: "left",
  },
  { display: "Application used", key: "apps", textAlign: "left" },
  {
    display: "Actions",
    key: "actions",
    textAlign: "left",
  },
];

const Likes = () => {
  const { plans, viewProject, removeLikes } = useContext(AppContext);
  const { user } = useContext(ProfileContext);
  const { queryUsers } = useUsers();
  const { queryLikes } = useLikes();
  const { queryWorks } = useWorks();
  const [dataState, setDataState] = useState(STATE.LOADING);
  const [likes, setLikes] = useState(undefined);
  const [work, setWork] = useState(undefined);
  const { screenSize } = useScreenSize();
  const toggleMobile = ["xs", "sm"].includes(screenSize);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [metadata, setMetadata] = useState(undefined);
  const location = useLocation();
  const navigate = useNavigate();
  const [shareProject, setShareProject] = useState(undefined);

  useEffect(() => {
    fetchData();
  }, [user, page, limit]);

  const fetchData = async () => {
    const { data: likes, _metadata } = await queryLikes(
      { user: user._id },
      page,
      limit
    );

    if (likes.length === 0) return;

    const workIds = likes[0].likes?.reverse();

    // TODO: Save metadata
    const { data: _works } = await queryWorks(
      { _id: { $in: workIds } },
      1,
      workIds.length,
      {
        banner: 1,
        name: 1,
        desc: 1,
        createdBy: 1,
        designType: 1,
        type: 1,
        program: 1,
        renderer: 1,
        active: 1,
        deleted: 1,
      }
    );
    setWork(_works);

    let userIds = [];

    for (const work of _works) {
      userIds.push(work.createdBy);
    }

    const { data: users } = await queryUsers(
      {
        _id: {
          $in: userIds,
        },
      },
      null,
      -1,
      { avatar: 1, firstname: 1, surname: 1, plan: 1 }
    );

    let _dataSource = workIds.map((workId) => {
      // * Map work
      let _work = _works.find((w) => w._id === workId);

      if (!_work) return null;

      const userFound = users.find((u) => u._id === _work.createdBy);

      const { avatar, firstname, surname, plan, userId } = userFound;

      const _unavailable = !_work.active || _work.deleted;

      return {
        ..._work,
        workId: _work._id,
        type: _work.type,
        project: (
          <div className="profile-likes-list-project">
            <img
              className={`profile-likes-list-project__banner ${
                _unavailable
                  ? "profile-likes-list-project__banner--inactive"
                  : ""
              }`}
              alt="work-banner"
              src={`${assetsUri}/${_work.banner}`}
              onError={({ target }) => {
                target.src = _work.banner;
              }}
            />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <div
                className={`profile-likes-list-project__name ${
                  _unavailable
                    ? "profile-likes-list-project__name--inactive"
                    : ""
                }`}
              >
                {_work.name}
              </div>
              {_unavailable && (
                <span className="profile-likes-list-project__alert">
                  Unvailable
                </span>
              )}
            </div>
          </div>
        ),
        createdBy: (
          <div className="profile-likes-list-user">
            <img
              className="profile-likes-list-user__avatar"
              alt="follower-avatar"
              src={`${assetsUri}/${avatar}`}
              onError={({ target }) => {
                target.src = avatar;
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "flex-start",
                gap: 8,
              }}
            >
              <div className="profile-likes-list-user__name">
                {firstname} {surname || ""}
              </div>
            </div>
          </div>
        ),
        apps: (
          <div className="profile-likes-list-apps">
            {_work.program ? (
              <img src={WORK_PROGRAMS[_work.program]} width={24} />
            ) : (
              <></>
            )}
            {_work.renderer ? (
              <img src={WORK_RENDERERS[_work.renderer]} width={24} />
            ) : (
              <></>
            )}
          </div>
        ),
        actions: (
          <div className="profile-likes-list-actions">
            <UnlikeSvg
              height={20}
              data-tooltip-id="tooltip-detail"
              data-tooltip-content="Unlike"
              data-tooltip-place="top"
              onClick={(e) => {
                e.stopPropagation();
                handleUnlikeClick(_work._id);
              }}
            />
            {!_unavailable && (
              <ShareSvg
                height={20}
                data-tooltip-id="tooltip-detail"
                data-tooltip-content="Share"
                data-tooltip-place="top"
                onClick={(e) => {
                  e.stopPropagation();

                  let typePath =
                    _work.type === 1 ? "projects" : "work-exchanges";
                  const link = `${window.location.origin}/${typePath}/${workId}`;

                  setShareProject(link);
                }}
              />
            )}
          </div>
        ),
      };
    });

    setLikes(_dataSource.filter((l) => !!l));
    setMetadata(_metadata);
    setDataState(STATE.IDLE);
  };

  const handleUnlikeClick = async (workId) => {
    await removeLikes(workId);

    fetchData();
  };

  const handleViewClick = (workId, type) => {
    // * Skip viewing project if unavailable
    const _work = likes.find((l) => l._id === workId);

    if (!_work.active || _work.deleted) {
      toast.warn("Project is unvailable");
      return;
    }

    if (type === 1 || type === 3) {
      viewProject(workId, location);
      return;
    }

    navigate(`/work-exchanges/${workId}`);
  };

  return (
    <div
      className={`profile-likes ${toggleMobile ? "profile-likes--mobile" : ""}`}
    >
      <List
        className="profile-likes-list"
        contentState={dataState}
        columns={columns}
        dataSource={likes}
        scrollable={true}
        viewable={true}
        onRecordClick={({ workId, type }) => {
          handleViewClick(workId, type);
        }}
      >
        <div>
          <SecondaryPagination
            limits={[10, 50]}
            defaultPage={metadata?.currentPage ?? 1}
            defaultLimit={limit}
            pages={metadata?.totalPages ?? 1}
            onPageChange={(page) => setPage(page)}
            onLimitChange={(limit) => setLimit(limit)}
          />
        </div>
      </List>
      <Tooltip.Tooltip id="tooltip-detail" />
      {shareProject && (
        <Share link={shareProject} onClose={() => setShareProject(undefined)} />
      )}
    </div>
  );
};

Likes.propTypes = {};

export default Likes;
